import { useStore } from '@store/index';
import { buildOrgRoutePath, buildRoutePath, removeOrgPrefix } from '@utils/routes';
import React from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

interface AnalyticsRouteValidatorProps {
  /** Access hook for the page being guarded (it also drives that page's sidebar entry). */
  useAccess: (args: { organizationId: string | null; programId: null }) => {
    shouldShow: boolean;
    isLoading: boolean;
  };
}

/**
 * Validates that the Analytics page should be accessible based on the user's
 * scoped roles. Waits for the role/permission query to complete before
 * deciding so we don't briefly flash the analytics shell to unauthorized
 * users.
 *
 * Mirrors `QcRouteValidator`. The page's access hook
 * (e.g. `useShouldShowAnalyticsPage`) controls the sidebar entry; this validator enforces the same gate for
 * direct URL access (e.g. typed/bookmarked `/org-{id}/analytics`).
 */
export function AnalyticsRouteValidator({ useAccess }: AnalyticsRouteValidatorProps) {
  const { selectedOrganizationId, selectedProgramId } = useStore();
  const { orgId } = useParams<{ orgId?: string }>();
  const routeOrganizationId = orgId ? removeOrgPrefix(orgId) : null;
  const effectiveOrganizationId = routeOrganizationId || selectedOrganizationId;
  const { shouldShow, isLoading } = useAccess({
    organizationId: effectiveOrganizationId,
    programId: null,
  });

  if (isLoading) {
    return null;
  }

  if (!shouldShow || !effectiveOrganizationId) {
    if (effectiveOrganizationId && selectedProgramId) {
      return (
        <Navigate
          to={buildRoutePath('/cases', effectiveOrganizationId, selectedProgramId)}
          replace
        />
      );
    }

    if (effectiveOrganizationId) {
      return <Navigate to={buildOrgRoutePath('', effectiveOrganizationId)} replace />;
    }

    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
